import { can, type Permission } from '@hrms/shared'
import type { ReactNode } from 'react'
import { LuLock, LuTriangleAlert } from 'react-icons/lu'
import { Link, Navigate, useLocation } from 'react-router'
import { Button, buttonClass } from '../components/ui/Button.tsx'
import { EmptyState } from '../components/ui/EmptyState.tsx'
import { Logo } from '../shell/Brand.tsx'
import { useAuth, useCurrentUser } from './AuthProvider.tsx'

function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center" aria-busy="true">
      <Logo className="h-[26px] animate-pulse" />
      <span className="sr-only">Loading…</span>
    </div>
  )
}

/** Signed-out visitors go to /login (and come back to where they were afterwards). */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, status, refetch } = useAuth()
  const location = useLocation()
  if (status === 'loading') return <Splash />
  if (status === 'error' && !user)
    return (
      <div className="mx-auto max-w-[640px] px-4 py-16">
        <EmptyState
          icon={LuTriangleAlert}
          color="orange"
          title="Can’t reach HR desk"
          action={<Button onClick={refetch}>Try again</Button>}
        >
          The server didn’t answer. Check your connection and try again.
        </EmptyState>
      </div>
    )
  if (!user) {
    const next = location.pathname + location.search
    return (
      <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />
    )
  }
  return children
}

export function ForbiddenPage() {
  return (
    <EmptyState
      icon={LuLock}
      color="amber"
      title="You don’t have access to this page"
      action={
        <Link to="/" className={buttonClass('primary')}>
          Go to my home page
        </Link>
      }
    >
      Your role doesn’t include this part of HR desk. If you think it should, ask an HR lead.
    </EmptyState>
  )
}

/** Shows a friendly 403 when the signed-in user lacks `permission`. The server checks again. */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission
  children: ReactNode
}) {
  const user = useCurrentUser()
  return can(user, permission) ? children : <ForbiddenPage />
}
